import React, { useContext, useState, useEffect } from "react";
import { TextField } from "@fluentui/react";
import studyviewerContext from "../../../../context/studyviewer/StudyviewerContext";
import ViewportContext     from "../../../../context/studyviewer/ViewportContext";
import themeContext       from "../../../../context/theme/ThemeContext";
import toastContext       from "../../../../context/toast/ToastContext";
import userContext        from "../../../../context/users/UserContext";
import { TOAST_TYPE }     from "../../../../constants/Constants";
import ModalMain   from "../../../../components/Modal/ModalMain";
import ModalHeader from "../../../../components/Modal/ModalHeader";
import ModalFooter from "../../../../components/Modal/ModalFooter";
import { getViewport, getCurrentOuterIndex } from "../../helpers/CornerstoneViewportService";
import {
  collectKOSavePayload,
  saveKOAnnotations,
} from "../../helpers/KOModalityService";
import "../../styles/styles.css";

// KO Document Title is no longer chosen in the UI — every Key Object is saved
// with the standard default "Of Interest" (DCM 113000).
const DEFAULT_KO_TITLE_CODE    = "113000";
const DEFAULT_KO_TITLE_MEANING = "Of Interest";

/**
 * Star button that opens a "Mark as Key Object" dialog (same style as the
 * Save as PR dialog) and posts a Key Object Selection Document to the
 * backend save-ko endpoint, referencing the currently displayed image.
 */
const MarkAsKOButton = ({ viewportIndex }) => {
  const { studyParentId, waitForSeriesReadyAndMerge, setKeyImagePresent, setKeyObjectImages } = useContext(studyviewerContext);
  const { activeViewportIndex, viewportSeriesData } = useContext(ViewportContext);
  const { getThemeColor } = useContext(themeContext);
  const { showToastv2 }   = useContext(toastContext);
  const { name, username } = useContext(userContext);

  // Creator is auto-filled from the logged-in user (no visible field in the KO dialog).
  const creatorDisplayName = name || username || "";

  const [saving,      setSaving]      = useState(false);
  const [status,      setStatus]      = useState(null); // null | "success" | "error"
  const [dialogOpen,  setDialogOpen]  = useState(false);
  const [description, setDescription] = useState("");

  const openDialog = () => {
    setDescription("");
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  // Ctrl+K keyboard shortcut → open the Mark-as-KO dialog, matching the Advance
  // viewer (MedDream). MarkAsKOButton is rendered once per viewport, so only the
  // instance for the ACTIVE viewport reacts — this opens exactly one popup and
  // targets the frame the user is looking at.
  useEffect(() => {
    if (viewportIndex !== activeViewportIndex) return;
    const onKeyDown = (e) => {
      // Ctrl+K (Cmd+K on Mac). Ignore auto-repeat and when a modal is already open.
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey &&
          (e.key === "k" || e.key === "K")) {
        e.preventDefault();   // stop the browser's own Ctrl+K (address/search bar)
        e.stopPropagation();
        if (!dialogOpen) openDialog();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportIndex, activeViewportIndex, dialogOpen]);

  const handleSave = async () => {
    closeDialog();
    if (saving) return;
    setSaving(true);
    setStatus(null);
    try {
      const vp = await getViewport(viewportIndex);
      if (!vp) throw new Error("Viewport not available");
      const imageId = vp.getCurrentImageId();
      if (!imageId) throw new Error("No image loaded in viewport");

      const payload = collectKOSavePayload(vp, imageId, studyParentId, {
        titleCode:    DEFAULT_KO_TITLE_CODE,
        titleMeaning: DEFAULT_KO_TITLE_MEANING,
        description,
        creatorName: creatorDisplayName,
      });
      const saveResponse = await saveKOAnnotations(payload);
      setStatus("success");
      showToastv2(TOAST_TYPE.SUCCESS, "Key Object saved successfully");
      setTimeout(() => setStatus(null), 2500);
      // The marked image is now a key image regardless of when/whether the KO
      // series itself finishes ingesting — flip this immediately rather than
      // waiting on the poll below (report-menu highlighting reads this flag).
      setKeyImagePresent?.(true);

      // Flip the viewport's star icon for THIS image immediately too.
      // keyObjectDescription is derived server-side across the whole study when
      // fetching by series — waitForSeriesReadyAndMerge below only fetches the
      // brand-new KO series' own images (the KO document itself, which it then
      // skips anyway — KO series are never added as a viewable stack), never the
      // source series this image belongs to, so it never picks up the flag.
      // Without this the star wouldn't appear until a full page refresh.
      //
      // Key this off currentStack.imageIds[currentIndex] rather than
      // vp.getCurrentImageId() — the star/badge checks (ViewportNavWidget,
      // Thumbnail) compare against that exact stack array, and cornerstone's
      // own runtime imageId isn't guaranteed to be the byte-identical string
      // (loader URL normalization, multi-frame swaps, etc.), so comparing
      // against anything else risks a silent mismatch.
      //
      // On a multi-frame clip the viewport index is a FRAME, not a series slot:
      // a mixed series reports its instance via getCurrentOuterIndex, and a
      // single-instance clip is always slot 0 (same as SaveAsPRButton).
      const currentStack = viewportSeriesData?.[viewportIndex]?.stack;
      const outerIdx = getCurrentOuterIndex("viewport_" + viewportIndex);
      const currentIdx = outerIdx != null
        ? outerIdx
        : currentStack?.imageIds?.length === 1 ? 0 : vp.getCurrentImageIdIndex?.();
      const stackImageUrl = currentStack?.imageIds?.[currentIdx] ?? imageId;
      setKeyObjectImages?.((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        { imageUrl: stackImageUrl, description },
      ]);

      // Wait for async DICOM ingestion to finish, then fetch + merge in the new KO
      // series — not awaited here so the button re-enables immediately. Note KO
      // series are never added as a viewable stack (mirrors generateStudyData's
      // own KO skip), so this mainly guards against/reports ingestion failure.
      const processId = saveResponse?.data?.data?.processId;
      if (processId && typeof waitForSeriesReadyAndMerge === "function") {
        waitForSeriesReadyAndMerge(processId, payload.newSeriesInstanceUID).then((result) => {
          if (!result?.success) {
            console.warn("[MarkAsKOButton] Key Object ingestion did not complete:", result);
          }
        });
      }
    } catch (err) {
      console.error("[MarkAsKOButton] Failed:", err);
      setStatus("error");
      const serverMessage = err?.response?.data?.message;
      if (serverMessage) {
        console.warn("[MarkAsKOButton] Server rejected Key Object save:", serverMessage);
      }
      // OLD: showToastv2(TOAST_TYPE.ERROR, "Failed to save Key Object");
      // NEW:
      showToastv2(TOAST_TYPE.ERROR, serverMessage || "Failed to save Key Object");
      setTimeout(() => setStatus(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  // ── Fluent UI control styles (dark theme) ──────────────────────────────────
  const fieldStyles = {
    fieldGroup: { backgroundColor: "transparent", border: "1px solid #605e5c" },
    field:      { color: getThemeColor("primary-color"), backgroundColor: "transparent" },
    label:      { color: getThemeColor("primary-color"), fontWeight: 600, fontSize: 12 },
  };
  // ── Modal definition (ModalMain / ModalHeader / ModalFooter pattern) ────────
  const modalDetails = {
    header: (
      <ModalHeader
        updatedModal={true}
        showPatientDetails={false}
        heading="Mark as Key Object"
        handleCancel={closeDialog}
      />
    ),
    footer: (
      <ModalFooter
        DefaultButtonList={[{ title: "Cancel", onclick: closeDialog }]}
        PrimaryButtonList={[{ title: "Save",   onclick: handleSave  }]}
      />
    ),
    body: (
      <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "14px" }}>
        <TextField
          label="Description"
          value={description}
          onChange={(_, v) => setDescription(v ?? "")}
          placeholder="e.g. Suspicious nodule, upper-right lobe"
          maxLength={64}
          styles={fieldStyles}
        />
        {/* Creator Name field removed from the KO dialog per request; the creator is
            still recorded automatically from the logged-in user in the save payload. */}
      </div>
    ),
    width: "400px",
  };

  const title =
    saving               ? "Saving Key Object…"
    : status === "success" ? "Key Object saved"
    : status === "error"   ? "Failed to save Key Object"
    :                        "Mark this slice as a Key Object";

  return (
    <>
      {dialogOpen && (
        <ModalMain showModal={dialogOpen} modalDetails={modalDetails} />
      )}

      <div>
        <span
          className="ko-mark-button"
          onClick={openDialog}
          title={title}
          style={{ opacity: saving ? 0.5 : 1, cursor: saving ? "wait" : "pointer" }}
        />
      </div>
    </>
  );
};

export default MarkAsKOButton;
